import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const DrugAndAlcoholTest = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const [dob, setDob] = useState("");
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name, dob } = res.data;
        setUsername(`${first_name} ${last_name}`);

        if (dob) {
          const date = new Date(dob);
          const formattedDob = `${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date
            .getDate()
            .toString()
            .padStart(2, "0")}/${date.getFullYear()}`;
          setDob(formattedDob);
        }
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 9; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "drug_and_alcohol";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey]
          );
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, userId, isAdmin, effectiveUserId]);

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Drug and Alcohol Test Consent and Release
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>
      <div
        className="form-wrapper my-4"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Drug and Alcohol Test Consent and Release</h2>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        <p className="mt-4">
          I, <strong>{username}</strong>, Born the date of; {dob}, hereby give
          my consent to and authorize New Way Trucking, its designated
          representatives, the testing laboratory designated by New Way Trucking
          and the designated employees of the laboratory to collect, distribute,
          and test any sample I provide in order to determine the presence or
          absence of substance(s) I consent to and understand that I may be
          asked to participate in substance testing at the discretion of New Way
          Trucking and its designated representatives for reasons outlined in
          the drug and alcohol policy of New Way Trucking.
        </p>
        <p className="mt-4">
          I give my consent to release the results of the test(s) and other
          medical information from the designated laboratory to New Way
          Trucking. I hereby provide consent to New-Way Trucking to conduct a
          limited query of the FMCSA Commercial Driver’s Licence Drug and
          Alcohol Clearinghouse to determine whether drug or alcohol violation
          information about me exists in the Clearinghouse.{" "}
        </p>
        <p className="mt-4">
          I understand that a positive test for illegal drugs, my refusal to
          test(s), produce a specimen, or tamper with a specimen may result in
          disciplinary action up to and including termination of employment.
        </p>
        <p className="mt-4">
          I hereby give my consent to release the results of the administered
          test to the person(s) or department(s) or the specified agent of New
          Way Trucking, including New Way Trucking’s Workers’ Compensation
          Insurance Company, for the purpose of determining the presence of
          alcohol and/or other drugs in my body for the duration of my
          employment.
        </p>
        <p className="mt-4">
          I understand that if I am injured during the course and scope of my
          employment and I test positive for the presence of alcohol and/or
          drugs, I may forfeit my eligibility for medical and indemnity
          benefits.
        </p>
        <p className="mt-4">
          I release to New Way Trucking and or New Way Trucking’s designated
          representative the results of the test(s) to which I have consented.
        </p>
        <p className="mt-4">
          I further authorize New Way Trucking and its designated representative
          to discuss the results with medical personnel/physician collecting the
          specimen, the testing facility, its directors, officers, agents, and
          employees responsible for administering and evaluating the
          aforementioned test(s)
        </p>
        <p className="mt-4">
          I also authorize the New Way Trucking to discuss the results with its
          legal advisors and to use the test results as a defence to any legal
          action to which I am a party.
        </p>
        <p className="mt-4">
          I further release any testing facility or any physicians who have
          tested me from any liability arising from a release of any and all
          results, written reports, medical records, and data concerning my
          test(s) to New Way Trucking and its designated representatives.
        </p>
        <p className="mt-4">
          I further understand that if I refuse to provide consent for New-Way
          Trucking Ltd. to conduct a limited query of the Clearinghouse, New-Way
          Trucking Ltd. must prohibit me from performing safety–sensitive
          functions, including driving a commercial motor vehicle, as required
          by FMCSA’s drug and alcohol program regulations.{" "}
        </p>
        <p className="mt-4">
          I have read, understand, agree and consent to all conditions and
          statements contained within this document.
        </p>

        <hr className="my-5" />

        <form>
          <div className="row mb-3 mt-5">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="drug_and_alcohol"
              taskId={9}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="drug_and_alcohol"
              taskId={9}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DrugAndAlcoholTest;
